/**
 * Morse — der Reiter „Tabelle“: alle Zeichen zum Nachschlagen und Anhören. (06.10.2026)
 *
 * Martin: „Was mir fehlt, ist beim Morse-Code auch eine Tabelle. So ist es mir noch viel zu schwer.“
 *
 *   - Aufgeräumt (08.10.2026, Martin: „so unaufgeräumt, mir wird übel“): von oben nach unten nur noch der Satz des Reiters,
 *     EIN Bedienstreifen (die Ordnung links, „Alle der Reihe nach“ rechts, am Telefon darunter), eine schlanke
 *     Zeile „● kurz  ▬ lang · Antippen spielt das Zeichen“ — dann die Zeichen. Alle Felder gleich: eine Höhe, ein Grund,
 *     ein dünner Rand, der Stand als Wort und als Fuß-Balken (leer, gestrichelt, wo es noch keinen Stand gibt).
 *   - Oben ein Satz, wie man liest: Punkt kurz, Strich lang — mit dem Bild, das überall gilt.
 *   - Alle Zeichen der Tabelle (A–Z, Ziffern, Satzzeichen, Ä Ö Ü ß, CH), je Karte: das Zeichen, sein Muster als Bild
 *     (Kreis und Balken, morse-bild.ts), die Silben („dah-di-dah“, aus dem Muster gerechnet), das geprüfte Merkwort
 *     („Kor-ri-dor“, morse-merkwort.ts — die Tabelle ist zum Nachschlagen, es steht immer da) und der Stand.
 *   - Antippen spielt das Zeichen (der eine Ton-Spieler, morse-ton.ts: gleiche Regeln — der Kontext schläft danach,
 *     ein neuer Tipp beendet den alten Ton sofort, Beginn und Ende gehen an den Seiten-Ton-Melder); das Bild
 *     leuchtet mit.
 *   - Ordnung wählbar: „Meine Reihenfolge“ (Koch: deine Zeichen, was noch kommt, was nicht im Kurs ist),
 *     „Alphabet“, „Nach Muster“ (nach Länge, je Länge in der Reihenfolge des Morse-Baums — E T, I A N M …).
 *   - Der Stand je Zeichen in FORM und WORT, nie nur Farbe: „87 % sicher“ mit schmalem Balken am Fuß, das neue mit der
 *     kleinen Marke „neu“, spätere mit „ab Stufe 12“, Zeichen außerhalb des Kurses mit „nicht im Kurs“ — wo es noch keinen
 *     Stand gibt, steht am Fuß eine gestrichelte Linie statt des Balkens (gleiche Höhe für alle).
 *   - Lief eine Übung, als man in die Tabelle kam, führt „Zurück zur Übung“ dorthin und setzt sie fort (morse-view.ts);
 *     sonst gibt es keinen Knopf — der Reiter „Hören“/„Geben“ darüber leistet dasselbe.
 *   - „Alle der Reihe nach“ (07.10.2026, Hörspur): spielt die Zeichen in der gewählten Ordnung vor; die Karte des
 *     gespielten Zeichens leuchtet im Takt wie beim Antippen und bleibt sichtbar (`data-spur="jetzt"`, ins Bild
 *     geholt). Der Abspieler ist der der Hörspur (morse-hoerspur.ts) — hier nur die Karten dazu.
 */
import { KOCH_REIHENFOLGE, kochNeueZeichen, kochStufe, kochStufeVon } from "../../shared/morse-koch.js";
import { MORSE_TABELLE, musterSilben } from "../../shared/morse.js";
import { leuchte, musterBild, musterSprechen } from "./morse-bild.js";
import type { SpurEreignis } from "./morse-hoerspur.js";
import { merkwortAria, merkwortElement } from "./morse-merkwort.js";
import { tabellenListe, type HoerListe } from "./morse-hoerspur-liste.js";
import { HOERSPUR, TABELLE } from "./morse-texte.js";
import { baueMeldung, el, gueteBalken, hoereMitMeldung, insBild, knopf, meldeIn, prozent, wahl, type MorseStandDaten, type MorseUmgebung } from "./morse-teile.js";

type TabellenOrdnung = "koch" | "alphabet" | "muster";
interface TabellenGruppe {
  titel: string;
  hinweis?: string;
  zeichen: string[];
}
/** Der Stand eines Zeichens in der Tabelle. */
type ZeichenStatus = "dabei" | "neu" | "spaeter" | "extra";

const ORDNUNG_SCHLUESSEL = "vespera.morse.tabelle.ordnung";
/** Alle Zeichen der Tabelle, in ihrer Reihenfolge (A–Z, Ziffern, Satzzeichen, Ä Ö Ü ß, CH). */
const ALLE = Object.keys(MORSE_TABELLE);
const BUCHSTABEN = ALLE.filter((z) => /^[A-Z]$/.test(z));
const ZIFFERN = ALLE.filter((z) => /^[0-9]$/.test(z));
const UMLAUTE = ["Ä", "Ö", "Ü", "ß", "CH"].filter((z) => MORSE_TABELLE[z] !== undefined);
const SATZZEICHEN = ALLE.filter((z) => !BUCHSTABEN.includes(z) && !ZIFFERN.includes(z) && !UMLAUTE.includes(z));

/** Ein Muster als Zahl im Morse-Baum: Punkt = links (0), Strich = rechts (1). */
function baumWert(muster: string): number {
  return [...muster].reduce((w, x) => w * 2 + (x === "-" ? 1 : 0), 0);
}

/** Die Gruppen der Tabelle in der gewählten Ordnung. Jedes Zeichen der Tabelle steht genau einmal darin (Test). */
export function tabellenGruppen(ordnung: TabellenOrdnung, stufe: number): TabellenGruppe[] {
  if (ordnung === "alphabet") {
    return [
      { titel: TABELLE.gruppen.buchstaben, zeichen: BUCHSTABEN },
      { titel: TABELLE.gruppen.umlaute, zeichen: UMLAUTE },
      { titel: TABELLE.gruppen.ziffern, zeichen: ZIFFERN },
      { titel: TABELLE.gruppen.satzzeichen, zeichen: SATZZEICHEN },
    ];
  }
  if (ordnung === "muster") {
    const je = new Map<number, string[]>();
    for (const z of ALLE) {
      const n = MORSE_TABELLE[z].length;
      je.set(n, [...(je.get(n) ?? []), z]);
    }
    return [...je.keys()]
      .sort((a, b) => a - b)
      .map((n) => {
        const hinweis = TABELLE.gruppen.laengeHinweis(n);
        return {
          titel: TABELLE.gruppen.laenge(n),
          ...(hinweis ? { hinweis } : {}),
          zeichen: je.get(n)!.sort((a, b) => baumWert(MORSE_TABELLE[a]) - baumWert(MORSE_TABELLE[b])),
        };
      });
  }
  const s = kochStufe(stufe);
  const kurs = KOCH_REIHENFOLGE.filter((z) => MORSE_TABELLE[z] !== undefined);
  return [
    { titel: TABELLE.gruppen.deine(s), hinweis: TABELLE.gruppen.deineHinweis, zeichen: kurs.slice(0, s + 1) },
    { titel: TABELLE.gruppen.spaeter, zeichen: kurs.slice(s + 1) },
    { titel: TABELLE.gruppen.extra, hinweis: TABELLE.gruppen.extraHinweis, zeichen: ALLE.filter((z) => !kurs.includes(z)) },
  ].filter((g) => g.zeichen.length > 0);
}

/** Der Stand eines Zeichens auf dieser Stufe: dabei (schon gelernt), neu, später oder nicht im Kurs. */
export function zeichenStatus(z: string, stufe: number): ZeichenStatus {
  const ab = kochStufeVon(z);
  if (ab === null) return "extra";
  if (kochNeueZeichen(stufe).includes(z)) return "neu";
  return ab <= kochStufe(stufe) ? "dabei" : "spaeter";
}

export class MorseTabelle {
  private readonly ordnungWahl: ReturnType<typeof wahl<TabellenOrdnung>>;
  private readonly gruppen: HTMLElement;
  private readonly zurueck: HTMLButtonElement;
  private readonly meldung: HTMLElement;
  private ordnung: TabellenOrdnung = "koch";
  /** Was gerade leuchtet — ein neuer Tipp beendet es. */
  private halt: () => void = () => undefined;
  /** Was zuletzt gezeichnet wurde — gleiche Stufe, gleiche Güte, gleiche Ordnung: nichts zu tun. */
  private gezeichnet = "";
  /** Das Zeichen, das die Hörspur gerade spielt (seine Karte ist markiert). */
  private spurZeichen: string | null = null;

  constructor(
    tafel: HTMLElement,
    private readonly u: MorseUmgebung,
    beiZurueck: () => void,
  ) {
    try {
      const g = localStorage.getItem(ORDNUNG_SCHLUESSEL);
      if (g === "koch" || g === "alphabet" || g === "muster") this.ordnung = g;
    } catch {
      /* Privatmodus */
    }
    tafel.classList.add("morse-tafel--tabelle");
    this.zurueck = knopf("", "", beiZurueck);
    this.zurueck.classList.add("morse-zurueck", "morse-tabelle-link");
    this.zurueck.hidden = true;

    // Ein div, kein p: die Haus-Regel `.view p { max-width: var(--mass) }` kürzte die Zeile und ließ den Knopf zu ihr hochrutschen.
    const lesen = el("div", "morse-tabelle-lesen");
    // Bild und Wort bleiben zusammen (je ein Stück, das nicht umbricht). Das Bild trägt keinen Namen: „Punkt“ und
    // „Strich“ stehen nur für Vorleser davor.
    const stueck = (muster: string, nur: string, wort: string): HTMLElement => {
      const bild = musterBild(muster, "m");
      bild.removeAttribute("role");
      bild.removeAttribute("aria-label");
      bild.setAttribute("aria-hidden", "true");
      const x = el("span", "morse-tabelle-lesen-stueck");
      x.append(bild, el("span", "morse-nur-vorlesen", `${nur} `), wort);
      return x;
    };
    // Die Leerzeichen dazwischen sieht niemand (Flex-Kinder), Vorleser und `textContent` lesen die drei Stücke getrennt.
    lesen.append(stueck(".", TABELLE.punktWort, TABELLE.punkt), " ", stueck("-", TABELLE.strichWort, TABELLE.strich), " ", el("span", "morse-tabelle-lesen-nach", TABELLE.lesenNach));

    this.ordnungWahl = wahl<TabellenOrdnung>(
      TABELLE.ordnung,
      (Object.keys(TABELLE.ordnungen) as TabellenOrdnung[]).map((id) => ({ id, text: TABELLE.ordnungen[id] })),
      (id) => {
        // Die Reihenfolge der Hörspur gilt für die Ordnung, mit der sie begann: ein Wechsel hält sie an.
        this.u.hoerspur?.stille("ordnung");
        this.ordnung = id;
        try {
          localStorage.setItem(ORDNUNG_SCHLUESSEL, id);
        } catch {
          /* Privatmodus */
        }
        this.zeichne();
      },
    );
    this.ordnungWahl.wurzel.classList.add("morse-wahl--voll");
    this.meldung = baueMeldung();
    this.gruppen = el("div", "morse-tabelle-gruppen");
    const spur = u.hoerspur?.platz({
      ort: "tabelle",
      start: HOERSPUR.tabelle.start,
      hinweis: () => HOERSPUR.tabelle.hinweis,
      liste: () => this.spurListe(),
      stil: "klein",
      kapitelKnoepfe: false,
    });
    // Reihenfolge = Lesefolge: Satz des Reiters (steht schon da), „Zurück zur Übung“, EIN Bedienstreifen (Ordnung,
    // „Alle der Reihe nach“), die Lesezeile, die Zeichen. Die Karte der Hörspur bleibt ein Kind der Tafel (klebt dort).
    tafel.append(this.zurueck, this.ordnungWahl.wurzel, ...(spur ? [spur.wurzel] : []), lesen, this.meldung, this.gruppen);
    u.hoerspur?.engine.hoere((e) => this.beiSpur(e));
  }

  /** Die Liste der Hörspur: alle Zeichen der Tabelle in der Ordnung, wie sie gerade gezeigt wird. */
  private spurListe(): HoerListe {
    const s = this.u.stand();
    const zeichen = tabellenGruppen(this.ordnung, s.stufe).flatMap((g) => g.zeichen);
    return tabellenListe({ zeichen, ordnung: TABELLE.ordnungen[this.ordnung] }, s.einstellungen);
  }

  /** Was die Hörspur meldet: welche Karte dran ist (markiert, im Bild) und im Takt leuchtet. Nur für ihre Tabellenliste. */
  private beiSpur(e: SpurEreignis): void {
    const eng = this.u.hoerspur?.engine;
    if (!eng) return;
    const meine = eng.aktiv && eng.liste?.id === "tabelle";
    if (!meine) {
      if (e.art === "zustand" || e.art === "ende") this.markiere(null);
      return;
    }
    // Pause: die Karte bleibt markiert, aber nichts leuchtet mehr im Takt.
    if (e.art === "zustand" && eng.pausiert) this.halt();
    if (e.art === "schritt") this.markiere(eng.aktuellerSchritt?.zeichen ?? null);
    else if (e.art === "ton" && e.schritt.zeichen !== undefined) {
      const bild = this.karte(e.schritt.zeichen)?.querySelector<HTMLElement>(".morse-bild");
      this.halt();
      if (bild) this.halt = leuchte(bild, e.plan, e.inMs);
    }
  }

  private karte(z: string): HTMLElement | null {
    for (const k of this.gruppen.querySelectorAll<HTMLElement>(".morse-tafelzeichen")) if (k.dataset.zeichen === z) return k;
    return null;
  }

  /** Eine Karte als „jetzt dran“ markieren (oder keine) und ins Bild holen — bei wenig Bewegung ohne weiches Scrollen. */
  private markiere(z: string | null): void {
    this.spurZeichen = z;
    for (const k of this.gruppen.querySelectorAll<HTMLElement>(".morse-tafelzeichen[data-spur]")) {
      delete k.dataset.spur;
      k.removeAttribute("aria-current");
    }
    if (z === null) {
      this.halt();
      delete this.gruppen.dataset.spur;
      return;
    }
    // Solange die Hörspur läuft, kleben ihre Karte und der Seitenkopf oben: die Karten halten Abstand (style.css).
    this.gruppen.dataset.spur = "an";
    const k = this.karte(z);
    if (!k) return;
    k.dataset.spur = "jetzt";
    k.setAttribute("aria-current", "true");
    // Erst nach dem nächsten Zeichnen: im selben Zug (Klick auf „Alle der Reihe nach“, Karte erscheint, Fokus springt)
    // verlor WebKit das weiche Scrollen zur ersten Karte (gemessen: sie blieb unten aus dem Fenster).
    const jetzt = (): void => {
      if (k.dataset.spur === "jetzt" && k.isConnected) insBild(k);
    };
    if (typeof requestAnimationFrame === "function") requestAnimationFrame(jetzt);
    else jetzt();
  }

  /** „Zurück zu …“ zeigen (Text) oder verbergen (`null`). */
  setzeZurueck(text: string | null): void {
    this.zurueck.hidden = text === null;
    if (text !== null) this.zurueck.textContent = text;
  }

  /** Den Ton der Tabelle beenden (Reiter verlassen). */
  still(): void {
    this.halt();
  }

  /** Nach Stand und Ordnung zeichnen (nur, wenn sich etwas geändert hat). */
  zeichne(): void {
    const s = this.u.stand();
    const schluessel = JSON.stringify([this.ordnung, s.stufe, s.zeichen.map((z) => [z.zeichen, z.gehoert, Math.round(z.guete * 100)])]);
    this.ordnungWahl.setze(this.ordnung);
    if (schluessel === this.gezeichnet) return;
    this.gezeichnet = schluessel;
    this.halt();
    // Neu gebaut wird nach einer Buchung (Güte) — wer mit der Tastatur auf einer Karte stand, bleibt dort.
    const fokus = document.activeElement instanceof HTMLElement && this.gruppen.contains(document.activeElement) ? document.activeElement.dataset.zeichen : undefined;
    this.gruppen.textContent = "";
    for (const g of tabellenGruppen(this.ordnung, s.stufe)) this.gruppen.appendChild(this.baueGruppe(g, s));
    if (fokus !== undefined) {
      for (const k of this.gruppen.querySelectorAll<HTMLElement>(".morse-tafelzeichen")) {
        if (k.dataset.zeichen === fokus) k.focus({ preventScroll: true });
      }
    }
    // Die Hörspur läuft weiter und hat ihre Karte noch: neu gebaut, wieder markieren.
    if (this.spurZeichen !== null) this.markiere(this.spurZeichen);
  }

  private baueGruppe(g: TabellenGruppe, s: MorseStandDaten): HTMLElement {
    const block = el("section", "morse-tabelle-gruppe");
    // Überschrift und Hinweis in EINER Kopfzeile (der Hinweis leise daneben, am Telefon darunter).
    const kopf = el("header", "morse-tabelle-kopf");
    const titel = el("h3", "morse-tabelle-titel", g.titel);
    titel.id = `morse-tabelle-${this.ordnung}-${g.titel.replace(/[^A-Za-z0-9]+/g, "-").toLowerCase()}`;
    block.setAttribute("aria-labelledby", titel.id);
    kopf.appendChild(titel);
    if (g.hinweis) kopf.appendChild(el("p", "morse-hinweis morse-tabelle-hinweis", g.hinweis));
    block.appendChild(kopf);
    const raster = el("div", "morse-tabelle-raster");
    raster.dataset.ordnung = this.ordnung;
    const je = new Map(s.zeichen.map((z) => [z.zeichen, z]));
    for (const z of g.zeichen) raster.appendChild(this.baueZeichen(z, s.stufe, je.get(z)));
    block.appendChild(raster);
    return block;
  }

  private baueZeichen(z: string, stufe: number, d: { gehoert: number; guete: number } | undefined): HTMLButtonElement {
    const m = MORSE_TABELLE[z] ?? "";
    const status = zeichenStatus(z, stufe);
    const gehoert = (d?.gehoert ?? 0) > 0;
    const stand =
      status === "extra"
        ? TABELLE.status.extra
        : status === "spaeter"
          ? TABELLE.status.spaeter(kochStufeVon(z)!)
          : gehoert
            ? TABELLE.status.sicher(prozent(d!.guete))
            : TABELLE.status.nichtGehoert;
    const b = el("button", "morse-tafelzeichen");
    b.type = "button";
    b.dataset.status = status;
    b.dataset.zeichen = z;
    const merkSatz = merkwortAria(z);
    b.setAttribute(
      "aria-label",
      `${z} anhören: ${musterSprechen(m)}, ${musterSilben(m)}.${merkSatz ? ` ${merkSatz}.` : ""} ${status === "neu" ? `${TABELLE.status.neu}, ` : ""}${stand}`,
    );
    const name = el("span", "morse-tafelzeichen-name", z);
    const bild = musterBild(m, "m");
    const kopf = el("span", "morse-tafelzeichen-kopf");
    kopf.append(name);
    if (status === "neu") kopf.append(el("span", "morse-tafelzeichen-marke", TABELLE.status.neu));
    const fuss = el("span", "morse-tafelzeichen-stand");
    fuss.append(el("span", "morse-tafelzeichen-stand-text", stand));
    // Jede Karte hat am Fuß genau EINEN Balken-Platz — gleiche Höhe für alle. Wo es einen Stand gibt, ist es der Balken;
    // sonst (noch nicht gehört, kommt noch, nicht im Kurs) eine leere, gestrichelte Linie (Form, keine Farbe).
    if ((status === "dabei" || status === "neu") && gehoert) {
      fuss.append(gueteBalken("morse-tafelzeichen-balken", d!.guete));
    } else {
      const leer = el("span", "morse-tafelzeichen-balken morse-tafelzeichen-balken--leer");
      leer.setAttribute("aria-hidden", "true");
      fuss.append(leer);
    }
    // Das Merkwort (15.10.2026) unter den Silben — wo es keins gibt (Satzzeichen, Umlaute), hält eine leere Zeile die Höhe.
    const merk = merkwortElement(z, "morse-tafelzeichen-merk") ?? el("span", "morse-merk morse-tafelzeichen-merk");
    merk.setAttribute("aria-hidden", "true");
    b.append(kopf, bild, el("span", "morse-tafelzeichen-silben", musterSilben(m)), merk, fuss);
    b.addEventListener("click", () => this.spiele(z, bild));
    return b;
  }

  /** Ein Zeichen spielen — das Bild leuchtet mit; ein neuer Tipp beendet den alten Ton (morse-ton.ts) und sein Leuchten. */
  private spiele(z: string, bild: HTMLElement): void {
    this.halt();
    meldeIn(this.meldung, "");
    // Je Tipp sein EIGENES Leuchten: das Ende eines abgebrochenen Tons darf das des neuen nicht löschen.
    let meins: () => void = () => undefined;
    void hoereMitMeldung(this.u, z, (t) => meldeIn(this.meldung, t, "fehler"), "reiter=tabelle abspielen", false, (plan, inMs) => {
      meins = leuchte(bild, plan, inMs);
      this.halt = meins;
    }).then(() => meins());
  }
}
